/* eslint-disable indent */
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';
import { toast } from 'layout';
import { createSpecializeReviewValidationSchema } from 'features/SpecializeReview/validation';
import { EditForm } from 'features/SpecializeReview/components';
import { TOAST } from 'constant';
import {
  getDetail,
  getGeneralProjectProposalInfo,
  update as updateSpecializeReview,
} from 'apis/specializeReviews.api';
import DynamicSpecializationForm from 'features/SpecializeReview/components/DynamicSpecializationForm';
import { is1593 } from 'utils/func';

export default function SpecializeReviewFormEdit() {
  const { t } = useTranslation();
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const { projectProposalId, councilId } = useParams();
  const [searchParams] = useSearchParams();
  const councilMemberId = searchParams.get('councilMemberId');

  const { data: dataGeneralProjectProposalInfo, isLoading: isLoadingProjectProposal } = useQuery(
    ['specialize-review', 'project-proposal', councilId, projectProposalId],
    ({ queryKey: [, , _councilId, _projectProposalId] }) =>
      getGeneralProjectProposalInfo(_councilId, _projectProposalId),
    { enabled: !!projectProposalId }
  );

  // binding data specialize review
  const {
    data: dataSpecializeReview,
    isLoading: isLoadingSpecializeReview,
    refetch: refetchSpecializeReview,
  } = useQuery(
    ['specialize-review', 'detail', councilMemberId, councilId, projectProposalId],
    ({ queryKey: [, , _councilMemberId, _councilId, _projectProposalId] }) =>
      getDetail(_councilMemberId, _councilId, _projectProposalId)
  );

  const specializeReviewData = useMemo(
    () => dataSpecializeReview?.data ?? {},
    [dataSpecializeReview]
  );
  // #endregion

  // #region Events

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', resolver: yupResolver(createSpecializeReviewValidationSchema) });

  const { isDirty, dirtyFields } = useFormState({ control });

  const {
    mutate,
    error: errorRes,
    isError: isErrorRes,
    isLoading: isLoadingUpdate,
  } = useMutation(updateSpecializeReview);

  const onSubmit = async (data) => {
    const reviewExpenditure = Object.keys(data?.expenditure)?.reduce((newArr, key) => {
      newArr.push({ expenditureTypeId: key, ...data?.expenditure?.[key] });
      return newArr;
    }, []);

    const submitData = {
      id: dataSpecializeReview?.data?.specializeReview?.id,
      projectProposalId,
      councilId,
      councilMemberId,
      reviewImportance: data?.reviewImportance,
      reviewQuality: data?.reviewQuality,
      reviewPicapability: data?.reviewPicapability,
      reviewConclusion: data?.reviewConclusion,
      reviewExpenditure: JSON.stringify(reviewExpenditure),
      expenditureTotalSuggestion: data?.expenditureTotalSuggestion,
      // eslint-disable-next-line no-nested-ternary
      scoreImportance: data?.score?.scoreImportance
        ? data?.score?.scoreImportance
        : (data?.score?.scoreCreativity ?? 0) + (data?.score?.scoreSuitableness ?? 0) !== 0
        ? (data?.score?.scoreCreativity ?? 0) + (data?.score?.scoreSuitableness ?? 0)
        : null,
      scoreQuality: data?.score?.scoreQuality,
      // eslint-disable-next-line no-nested-ternary
      scoreProduct: data?.score?.scoreProduct
        ? data?.score?.scoreProduct
        : (data?.score?.scoreAnnouncement ?? 0) + (data?.score?.scoreTraining ?? 0) !== 0
        ? (data?.score?.scoreAnnouncement ?? 0) + (data?.score?.scoreTraining ?? 0)
        : null,
      scoreCapability: data?.score?.scoreCapability,
      scoreSubCriteria: data?.score?.scoreSubCriteria,
      scoreCreativity: data?.score?.scoreCreativity,
      scoreSuitableness: data?.score?.scoreSuitableness,
      scoreAnnouncement: data?.score?.scoreAnnouncement,
      scoreTraining: data?.score?.scoreTraining,
    };

    mutate(submitData, {
      onSuccess: () => {
        refetchSpecializeReview();
        reset({}, { keepValues: true });
        toast(TOAST.SUCCESS, t('council.review.updateSuccess'));
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  useEffect(() => {
    if (specializeReviewData?.specializeReview) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { specializeReview } = specializeReviewData;
      // binding review data from specialize-review
      setValue('reviewImportance', specializeReview?.reviewImportance ?? null);
      setValue('reviewQuality', specializeReview?.reviewQuality ?? null);
      setValue('reviewPicapability', specializeReview?.reviewPicapability ?? null);
      setValue('reviewConclusion', specializeReview?.reviewConclusion ?? null);
      setValue('expenditureTotalSuggestion', specializeReview?.expenditureTotalSuggestion ?? null);

      // binding score data from specialize-review
      setValue('score', {});
      setValue('score.scoreCreativity', specializeReview?.scoreCreativity ?? 0);
      setValue('score.scoreSuitableness', specializeReview?.scoreSuitableness ?? 0);
      setValue(
        'score.scoreImportance',
        specializeReview?.scoreImportance ??
          (specializeReview?.scoreCreativity ?? 0) + (specializeReview?.scoreSuitableness ?? 0)
      );
      setValue('score.scoreQuality', specializeReview?.scoreQuality ?? 0);
      setValue('score.scoreAnnouncement', specializeReview?.scoreAnnouncement ?? 0);
      setValue('score.scoreTraining', specializeReview?.scoreTraining ?? 0);
      setValue(
        'score.scoreProduct',
        specializeReview?.scoreProduct ??
          (specializeReview?.scoreAnnouncement ?? 0) + (specializeReview?.scoreTraining ?? 0)
      );
      setValue('score.scoreCapability', specializeReview?.scoreCapability ?? 0);
      setValue('score.scoreSubCriteria', specializeReview?.scoreSubCriteria ?? 0);

      // binding expenditure data from specialize-review
      const expenditures = JSON.parse(specializeReview?.reviewExpenditure);
      // eslint-disable-next-line array-callback-return
      expenditures.map((item) => {
        setValue(`expenditure.${item?.expenditureTypeId}.high`, item?.high);
        setValue(`expenditure.${item?.expenditureTypeId}.low`, item?.low);
        setValue(`expenditure.${item?.expenditureTypeId}.suitable`, item?.suitable);
      });
      reset({}, { keepValues: true });
    }
  }, [specializeReviewData, specializeReviewData?.specializeReview, setValue]);

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  const template = useMemo(
    () =>
      dataGeneralProjectProposalInfo?.data?.template ||
      parseInt(dataGeneralProjectProposalInfo?.data?.template, 10) ||
      null,
    [dataGeneralProjectProposalInfo]
  );

  const renderForm = () => {
    if (is1593(template) || isLoadingProjectProposal) {
      return (
        <DynamicSpecializationForm
          projectProposalId={projectProposalId}
          councilMemberId={councilMemberId}
          councilId={councilId}
          generalInfo={dataGeneralProjectProposalInfo?.data}
          isLoading={isLoadingProjectProposal}
        />
      );
    }
    return (
      <EditForm
        isLoadingUpdate={isLoadingUpdate || isLoadingProjectProposal}
        isErrorRes={isErrorRes}
        errorRes={errorRes}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        control={control}
        watch={watch}
        setValue={setValue}
        getValues={getValues}
        isDirty={isDirty}
        dirtyFields={dirtyFields}
        errors={errors}
        isValid={isValid}
        isVisibleErrorDialog={isVisibleErrorDialog}
        setIsVisibleDialogError={setIsVisibleDialogError}
        specializeReviewData={specializeReviewData}
        isLoadingSpecializeReview={isLoadingSpecializeReview}
        reset={reset}
        dataGeneralProjectProposalInfo={dataGeneralProjectProposalInfo}
        projectProposalIdV2={projectProposalId}
      />
    );
  };
  // #endregion

  return (
    <>
      <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog
            visible={isActive}
            header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
            position="center"
            style={{ width: '300px' }}
            onHide={onCancel}
            draggable={false}
            footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
          >
            <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
          </Dialog>
        )}
      </ReactRouterPrompt>
      {renderForm()}
    </>
  );
}
